import { createContext, useContext } from "react";

import type { Socket } from "socket.io-client";

import type { AnalyticsEventDomain, User } from "@virtual-brain-lib/types";

export interface ChatOptions {
	readonly type?: "chat" | "chatbot" | "app" | undefined;
	readonly analyticsEventDomain?: AnalyticsEventDomain | undefined;
	readonly displayTips?: boolean | undefined;
	readonly sourcesDisplayType?: "link" | "extracts" | undefined;
	readonly displaySources?: boolean | undefined;
	readonly displaySuggestedPrompts?: boolean | undefined;
	readonly displayActions?: boolean | undefined;
	readonly botIcon?: string | undefined;
	readonly color?: string | undefined;
}

export interface IChatContext extends ChatOptions {
	readonly analyticsEventDomain: AnalyticsEventDomain;
	readonly user?: User | undefined;
	readonly socket?: Socket | undefined;
}

interface ChatProps {
	readonly user?: User | undefined;
	readonly children: React.ReactNode | React.ReactNode[];
	readonly options?: ChatOptions | undefined;
	readonly socket?: Socket | undefined;
}

const defaultOptions = {
	type: "chat",
	analyticsEventDomain: "Chat",
	displaySources: true,
	displaySuggestedPrompts: true,
	displayTips: true,
	displayActions: true,
	sourcesDisplayType: "extracts",
	botIcon: undefined,
} satisfies ChatOptions;

const ChatContext = createContext<IChatContext>({
	...defaultOptions,
});

export default function ChatProvider({ children, options, user, socket }: ChatProps) {
	const contextValue: IChatContext = {
		...defaultOptions,
		...options,
		analyticsEventDomain: options?.analyticsEventDomain ?? defaultOptions.analyticsEventDomain,
		user,
		socket,
	};

	return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
}

export function useChatContext() {
	const context = useContext(ChatContext);

	if (!context) {
		throw new Error("useAppContext must be used within a ChatProvider");
	}

	return context;
}
